/**
 * Team Member Modal
 *
 * Opens a <dialog> modal with full team member details when a
 * .card--team is clicked. All data is read from elements already
 * rendered by GenerateBlocks — no custom data attributes required
 * on the card markup.
 *
 * Post ID   → `post-{N}` class on the parent .gb-loop-item wrapper
 * Title     → text content of the card's <h3>
 * Image URL → src of the card's <img>
 * Bio, attributes, social links → fetched from the WP REST API
 */

const ENDPOINT = '/wp-json/wp/v2/team-members';
const cache = new Map();

/**
 * Initialise — called on DOMContentLoaded.
 */
function init() {
	const dialog = document.getElementById( 'team-member-modal' );
	if ( ! dialog ) return;

	// Close button
	const closeBtn = dialog.querySelector( '.team-modal__close' );
	if ( closeBtn ) {
		closeBtn.addEventListener( 'click', () => dialog.close() );
	}

	// Clicking the backdrop (native <dialog> receives the click when
	// the user clicks outside the inner panel)
	dialog.addEventListener( 'click', ( e ) => {
		if ( e.target === dialog ) dialog.close();
	} );

	// Wire up every team card
	document.querySelectorAll( '.card--team' ).forEach( ( card ) => {
		card.setAttribute( 'role', 'button' );
		card.setAttribute( 'tabindex', '0' );

		card.addEventListener( 'click', ( e ) => handleCardClick( e, dialog ) );

		card.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				e.preventDefault();
				handleCardClick( e, dialog );
			}
		} );
	} );
}

/**
 * Extract the WordPress post ID from the `post-{N}` class on the
 * nearest .gb-loop-item ancestor.
 *
 * @param  {HTMLElement} card
 * @return {string|null}
 */
function getPostId( card ) {
	const match = card.closest( '.gb-loop-item' )
	                  ?.className.match( /\bpost-(\d+)\b/ );
	return match ? match[ 1 ] : null;
}

/**
 * Handle a card click: pre-populate, open, then fetch.
 *
 * @param {Event}             e
 * @param {HTMLDialogElement} dialog
 */
async function handleCardClick( e, dialog ) {
	const card   = e.currentTarget;
	const postId = getPostId( card );
	if ( ! postId ) return;

	// Read title and image directly from the rendered card DOM
	const title    = card.querySelector( 'h3' )?.textContent?.trim() ?? '';
	const imageUrl = card.querySelector( 'img' )?.src ?? '';

	prePopulate( dialog, title, imageUrl );
	dialog.showModal();

	// Move focus into the modal for accessibility
	const closeBtn = dialog.querySelector( '.team-modal__close' );
	if ( closeBtn ) closeBtn.focus();

	try {
		const data = await fetchTeamMember( postId );
		populateModal( dialog, data );
	} catch ( err ) {
		console.error( 'Team modal: failed to load member data.', err );
	}
}

/**
 * Immediately fill name and image from the clicked card's DOM —
 * gives instant feedback before the REST fetch completes.
 *
 * @param {HTMLDialogElement} dialog
 * @param {string}            title
 * @param {string}            imageUrl
 */
function prePopulate( dialog, title, imageUrl ) {
	const nameEl    = dialog.querySelector( '.team-modal__name' );
	const imageEl   = dialog.querySelector( '.team-modal__image' );
	const attrsEl   = dialog.querySelector( '.team-modal__attributes' );
	const bioEl     = dialog.querySelector( '.team-modal__bio' );
	const socialRow = dialog.querySelector( '.team-modal__social' );

	if ( nameEl )  nameEl.textContent = title;

	if ( imageEl ) {
		imageEl.src = imageUrl;
		imageEl.alt = title;
	}

	// Show a subtle loading state while the API call is in flight
	if ( attrsEl )   attrsEl.innerHTML = '';
	if ( bioEl )     bioEl.innerHTML   = '<p class="team-modal__loading" aria-busy="true">&hellip;</p>';
	if ( socialRow ) socialRow.hidden  = true;
}

/**
 * Fetch a single team member from the REST API (results are cached).
 *
 * @param  {string} postId
 * @return {Promise<Object>}
 */
async function fetchTeamMember( postId ) {
	if ( cache.has( postId ) ) return cache.get( postId );

	const response = await fetch( `${ ENDPOINT }/${ postId }?_embed` );
	if ( ! response.ok ) {
		throw new Error( `REST API returned ${ response.status }` );
	}

	const data = await response.json();
	cache.set( postId, data );
	return data;
}

/**
 * Populate the modal with full data from the REST response.
 *
 * @param {HTMLDialogElement} dialog
 * @param {Object}            data   — WP REST API team_member object
 */
function populateModal( dialog, data ) {
	const attrsEl    = dialog.querySelector( '.team-modal__attributes' );
	const bioEl      = dialog.querySelector( '.team-modal__bio' );
	const linkedinEl = dialog.querySelector( '.team-modal__linkedin' );
	const emailEl    = dialog.querySelector( '.team-modal__email' );
	const socialRow  = dialog.querySelector( '.team-modal__social' );

	// Attributes (position, credentials, etc.)
	const attributes = data?.acf?.team_member_attributes ?? [];
	if ( attrsEl ) {
		attrsEl.innerHTML = attributes.length
			? attributes.map( ( attr ) => `<p class="team-modal__attribute">${ attr }</p>` ).join( '' )
			: '';
	}

	// Bio / post content
	if ( bioEl ) {
		bioEl.innerHTML = data?.content?.rendered ?? '';
	}

	// LinkedIn
	const linkedin = data?.acf?.team_member_linkedin;
	if ( linkedinEl ) {
		if ( linkedin ) {
			linkedinEl.href   = linkedin;
			linkedinEl.hidden = false;
		} else {
			linkedinEl.hidden = true;
		}
	}

	// Email
	const email = data?.acf?.team_member_email;
	if ( emailEl ) {
		if ( email ) {
			emailEl.href   = `mailto:${ email }`;
			emailEl.hidden = false;
		} else {
			emailEl.hidden = true;
		}
	}

	// Show social row only when at least one link exists
	if ( socialRow ) {
		socialRow.hidden = ! linkedin && ! email;
	}
}

document.addEventListener( 'DOMContentLoaded', init );
